/**
 * Como o app descobre a marca antes de qualquer login (A009).
 *
 * Ordem: `devSlug` → subdomínio → caminho (`/entrar/<slug>`) → guardado →
 * Eva. A função é pura para o teste não precisar de `window` nem de storage;
 * quem chama é que lê os dois e grava o resultado.
 *
 * ## Por que o caminho existe
 *
 * Em produção a marca vem do subdomínio, que é o desenho. Em localhost não há
 * subdomínio utilizável, e trocar `VITE_DEV_BRAND_SLUG` exige editar `.env` e
 * reiniciar o Vite — o que torna difícil olhar duas marcas em sequência.
 *
 * `/entrar/<slug>` resolve isso sem colidir com rota: `/entrar` é prefixo
 * fixo, então o segundo segmento nunca disputa com `/eu`, `/marca` e os
 * outros portais. O admin legado detectava pelo PRIMEIRO segmento e por isso
 * precisava de uma lista de reservados mantida por um script de build.
 *
 * ## Depois do login, quem manda é a sessão
 *
 * O caminho perde o slug assim que a pessoa sai de `/entrar/<slug>`. A
 * primeira versão guardava a marca no `localStorage` para cobrir isso — e
 * criou estado escondido sem ciclo de vida: não morria no logout, e quem
 * visitasse uma marca uma vez ficava nela até limpar o navegador.
 *
 * Agora o `sessionBrandId` cobre esse caso: logada, a marca é a do contexto
 * assumido, que desde a A027 é necessariamente o da porta. Sobra a janela
 * deslogada navegando entre telas públicas, e só ela usa `sessionStorage` —
 * que morre com a aba.
 *
 * `/entrar` SEM slug continua sendo a saída: volta para a Eva e manda apagar.
 */
import { PORTA_BASE } from '@eva/schemas';

export interface BrandDetection {
  /** `default` para a Eva; slug para as demais; o hostname num domínio personalizado. */
  identifier: string;
  kind: 'id' | 'slug' | 'host';
  source: 'subdomain' | 'dev' | 'path' | 'session' | 'stored' | 'base' | 'host';
  /**
   * O que quem chama deve gravar: o slug, `null` para apagar, ou `undefined`
   * para não mexer. Deixar a escrita fora daqui mantém a função pura.
   */
  lembrar?: string | null;
}

/**
 * A porta base — endereço sem subdomínio (A044). Qual marca ela mostra é
 * configuração do mantenedor; o servidor resolve `base` para ela. O cliente
 * não sabe (e não precisa saber) que hoje é a Eva.
 */
const BASE: BrandDetection = { identifier: PORTA_BASE, kind: 'id', source: 'base' };

/** `/entrar/<slug>` — e só ele. `/entrar` sozinho significa "voltar para a Eva". */
function slugDoCaminho(pathname: string): { slug: string | null } | null {
  const partes = pathname.split('/').filter(Boolean);
  if (partes[0] !== 'entrar') return null;
  return { slug: partes[1] ?? null };
}

export function detectBrand(opts: {
  hostname: string;
  baseDomain?: string | null;
  devSlug?: string | null;
  reserved?: readonly string[];
  /** Caminho atual. Sem ele, a detecção por caminho fica desligada. */
  pathname?: string | null;
  /** A marca do contexto assumido, quando há sessão. */
  sessionBrandId?: string | null;
  /** Marca da janela deslogada, guardada só nesta aba. */
  storedSlug?: string | null;
}): BrandDetection {
  const { hostname, baseDomain, devSlug, reserved = [], pathname, sessionBrandId, storedSlug } = opts;

  if (devSlug && devSlug.trim()) {
    return { identifier: devSlug.trim(), kind: 'slug', source: 'dev' };
  }

  // O subdomínio é o desenho de produção e vence tudo — inclusive apaga o que
  // estiver guardado, senão uma visita antiga contaminaria a marca do host.
  if (baseDomain && hostname.endsWith('.' + baseDomain)) {
    const slug = hostname.slice(0, -(baseDomain.length + 1));
    if (slug && !slug.includes('.') && !reserved.includes(slug)) {
      return { identifier: slug, kind: 'slug', source: 'subdomain', lembrar: null };
    }
  }

  /*
   * Domínio personalizado (A050): host que não é a base nem subdomínio dela,
   * nem localhost/IP. A marca é a dona do host — o servidor responde por
   * `/public/partners/by-host/<host>/branding`, e só domínio ATIVO resolve.
   * Vence caminho, sessão e guardado pelo mesmo motivo do subdomínio: o
   * endereço É a porta.
   */
  const ehLocal = hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname) || hostname.endsWith('.localhost');
  const ehABase = !!baseDomain && (hostname === baseDomain || hostname.endsWith('.' + baseDomain));
  // Os endereços que o próprio Firebase Hosting dá ao site não são de marca.
  const ehDoHosting = hostname.endsWith('.web.app') || hostname.endsWith('.firebaseapp.com');
  if (baseDomain && !ehLocal && !ehABase && !ehDoHosting && hostname.includes('.')) {
    return { identifier: hostname, kind: 'host', source: 'host', lembrar: null };
  }

  const doCaminho = pathname ? slugDoCaminho(pathname) : null;
  if (doCaminho) {
    const slug = doCaminho.slug;
    if (slug && !reserved.includes(slug)) {
      return { identifier: slug, kind: 'slug', source: 'path', lembrar: slug };
    }
    /*
     * `/entrar` sem slug é a saída — e é porta EXPLÍCITA da Eva, não ausência
     * de informação. A diferença importa para a regra que encerra a sessão ao
     * trocar de porta: estar logada numa marca e abrir `/entrar` puro deve
     * deslogar, e sem `source: 'path'` isso ficaria indistinguível de "abri
     * uma tela qualquer".
     */
    return { identifier: PORTA_BASE, kind: 'id', source: 'path', lembrar: null };
  }

  // A sessão vence o guardado: é a fonte com ciclo de vida de verdade.
  if (sessionBrandId && sessionBrandId.trim()) {
    return { identifier: sessionBrandId.trim(), kind: 'id', source: 'session' };
  }

  if (storedSlug && storedSlug.trim()) {
    return { identifier: storedSlug.trim(), kind: 'slug', source: 'stored' };
  }

  return BASE;
}
